import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Check, X, AlertCircle, Sparkles, ArrowLeft, ArrowRight, Star, Trophy, Shield, Users, Crown, ShieldCheck, Layers, Eye } from 'lucide-react';
import logo from '../assets/images/haythix-logo.png';
import PublicNav from '../components/PublicNav';
import PublicFooter from '../components/PublicFooter';
import Seo from '../components/Seo';

// ─── Status types ────────────────────────────────────────────────────────────

const YES = 'yes';
const NO = 'no';
const LTD = 'limited';
const UNQ = 'unique';

// ─── Competitor keys: c3=3Commas, ch=Cryptohopper, bg=Bitsgap, pn=Pionex, cr=Coinrule

const COMPETITORS = [
  { key: 'haythix', name: 'haythix', highlight: true },
  { key: 'c3',  name: '3Commas' },
  { key: 'ch',  name: 'Cryptohopper' },
  { key: 'bg',  name: 'Bitsgap' },
  { key: 'pn',  name: 'Pionex' },
  { key: 'cr',  name: 'Coinrule' },
];

// ─── Comparison data ─────────────────────────────────────────────────────────

const SECTIONS = [
  {
    category: 'The First Platform with DEX + CEX, Fully Unified',
    headline: true,
    features: [
      { name: 'Trade DEXs (Uniswap/PancakeSwap/etc.) AND CEXs (Binance/Coinbase/etc.) from one app', haythix: UNQ, c3: NO, ch: NO, bg: NO, pn: NO, cr: NO },
      { name: 'Unified balance view across all DEXs + CEXs + your wallets',                          haythix: UNQ, c3: NO, ch: NO, bg: LTD, pn: NO, cr: NO },
      { name: 'Run the same Smart Trade / Grid / DCA on either DEX or CEX',                          haythix: UNQ, c3: NO, ch: NO, bg: NO,  pn: NO, cr: NO },
      { name: 'Cross-venue arbitrage detection (DEX ↔ CEX)',                                          haythix: UNQ, c3: LTD, ch: NO, bg: LTD, pn: LTD, cr: NO },
      { name: '15 CEX integrations via CCXT (spot on all 15)',                                       haythix: UNQ, c3: LTD, ch: LTD, bg: LTD, pn: NO, cr: LTD },
      { name: '15 chains for DEX trading (14 EVM + Solana)',                                            haythix: YES, c3: NO, ch: NO, bg: NO, pn: NO, cr: NO },
      { name: 'Perpetual futures on 8 CEX venues (up to 125x)',                                       haythix: UNQ, c3: LTD, ch: LTD, bg: LTD, pn: NO, cr: NO },
      { name: 'Non-custodial DEX (your keys) + custodial CEX (via API keys you encrypt)',            haythix: UNQ, c3: NO, ch: NO, bg: NO, pn: NO, cr: NO },
      { name: 'Single tax/PnL view spanning both DEX & CEX trades',                                   haythix: UNQ, c3: NO, ch: NO, bg: LTD, pn: NO, cr: NO },
    ],
  },
  {
    category: 'Core Bot Features',
    features: [
      { name: 'Multi-bot support',          haythix: YES, c3: YES, ch: YES, bg: LTD, pn: YES, cr: LTD },
      { name: 'Up to 10 wallets per bot',   haythix: YES, c3: NO,  ch: NO,  bg: NO,  pn: NO,  cr: NO },
      { name: 'Bot cloning (1-click)',       haythix: YES, c3: LTD, ch: NO,  bg: NO,  pn: NO,  cr: NO },
      { name: 'DCA trading bot',            haythix: YES, c3: YES, ch: YES, bg: YES, pn: YES, cr: LTD },
      { name: 'Grid trading bot',           haythix: YES, c3: YES, ch: LTD, bg: YES, pn: YES, cr: NO },
      { name: 'Smart Trade Terminal (multi-TP + trailing SL)', haythix: YES, c3: YES, ch: YES, bg: LTD, pn: NO, cr: NO },
      { name: 'Conditional / regime-switching bots',           haythix: UNQ, c3: NO,  ch: LTD, bg: NO,  pn: NO, cr: LTD },
      { name: 'Algo orders (TWAP/VWAP/Iceberg/Bracket)',       haythix: UNQ, c3: NO,  ch: NO,  bg: NO,  pn: NO, cr: NO },
      { name: 'Signal bot (TradingView)',   haythix: YES, c3: YES, ch: YES, bg: LTD, pn: NO,  cr: YES },
      { name: 'Custom display names & IDs', haythix: YES, c3: NO,  ch: NO,  bg: NO,  pn: NO,  cr: NO },
      { name: 'Arbitrage bot',              haythix: YES, c3: LTD, ch: NO,  bg: YES, pn: YES, cr: NO },
      { name: 'Spot + futures trading',     haythix: YES, c3: YES, ch: YES, bg: YES, pn: YES, cr: LTD },
    ],
  },
  {
    category: 'AI & Intelligence',
    features: [
      { name: 'AI signals (Buy/Sell/Hold)',       haythix: YES, c3: LTD, ch: LTD, bg: LTD, pn: NO,  cr: LTD },
      { name: 'AI strategy generator (NLP)',      haythix: YES, c3: NO,  ch: NO,  bg: NO,  pn: NO,  cr: NO },
      { name: 'Natural Language bot builder',     haythix: UNQ, c3: NO,  ch: LTD, bg: NO,  pn: NO,  cr: LTD },
      { name: 'Monte Carlo simulations',          haythix: YES, c3: NO,  ch: NO,  bg: NO,  pn: NO,  cr: NO },
      { name: 'Bayesian probability engine',      haythix: YES, c3: NO,  ch: NO,  bg: NO,  pn: NO,  cr: NO },
      { name: 'Market regime classification',     haythix: YES, c3: NO,  ch: NO,  bg: NO,  pn: NO,  cr: NO },
      { name: 'Pattern recognition engine',       haythix: YES, c3: LTD, ch: LTD, bg: NO,  pn: NO,  cr: NO },
      { name: '8-factor risk intelligence',       haythix: UNQ, c3: NO,  ch: NO,  bg: NO,  pn: NO,  cr: NO },
      { name: 'Anomaly detection',                haythix: UNQ, c3: NO,  ch: NO,  bg: NO,  pn: NO,  cr: NO },
      { name: 'AI-powered market forecasts',      haythix: YES, c3: NO,  ch: LTD, bg: NO,  pn: NO,  cr: NO },
      { name: 'Multi-model AI engine',            haythix: UNQ, c3: NO,  ch: NO,  bg: NO,  pn: NO,  cr: NO },
      { name: 'Pre-canned stress scenario library', haythix: UNQ, c3: NO,  ch: LTD, bg: NO,  pn: NO,  cr: NO },
    ],
  },
  {
    category: 'Multi-Chain & DEX',
    features: [
      { name: 'DEX trading (Uniswap V3/V2, Aerodrome, CoW, SushiSwap, Curve, Balancer, PancakeSwap, Jupiter)', haythix: YES, c3: NO,  ch: NO,  bg: NO,  pn: NO,  cr: NO },
      { name: 'MEV-protected swap router (CoW, 0x, Paraswap, 1inch)',      haythix: UNQ, c3: NO,  ch: NO,  bg: NO,  pn: NO,  cr: NO },
      { name: 'DEX Limit Orders (pre-signed, gasless until fill)',         haythix: UNQ, c3: NO,  ch: NO,  bg: NO,  pn: NO,  cr: NO },
      { name: 'DEX Stop Orders (Chainlink oracle-triggered)',              haythix: UNQ, c3: NO,  ch: NO,  bg: NO,  pn: NO,  cr: NO },
      { name: 'DEX Smart Trade (limit + multi-TP + stop, on-chain)',       haythix: UNQ, c3: NO,  ch: NO,  bg: NO,  pn: NO,  cr: NO },
      { name: 'DEX Grid Bot (Uniswap V3 / Aerodrome / SushiSwap / Curve)', haythix: UNQ, c3: NO,  ch: NO,  bg: NO,  pn: NO,  cr: NO },
      { name: 'DEX DCA Bot (with safety orders + take-profit)',            haythix: UNQ, c3: NO,  ch: NO,  bg: NO,  pn: NO,  cr: NO },
      { name: 'DEX TradingView Webhooks (auto-execute on-chain)',          haythix: UNQ, c3: NO,  ch: NO,  bg: NO,  pn: NO,  cr: NO },
      { name: 'DEX Algo Orders (TWAP / VWAP / Iceberg on-chain)',          haythix: UNQ, c3: NO,  ch: NO,  bg: NO,  pn: NO,  cr: NO },
      { name: 'DEX Conditional Bot (regime-switching across DEX bots)',    haythix: UNQ, c3: NO,  ch: NO,  bg: NO,  pn: NO,  cr: NO },
      { name: 'DEX Cross-chain Arbitrage (Base vs Polygon vs Arbitrum +)', haythix: UNQ, c3: NO,  ch: NO,  bg: NO,  pn: NO,  cr: NO },
      { name: 'Uniswap V3 Concentrated Liquidity Studio',                  haythix: UNQ, c3: NO,  ch: NO,  bg: NO,  pn: NO,  cr: NO },
      { name: '15 chains supported (ETH/BSC/Polygon/Arb/Base/AVAX/OP/Solana/Cronos/Fantom/zkSync/Mantle/Linea/Scroll/Blast)', haythix: YES, c3: NO,  ch: NO,  bg: NO,  pn: NO,  cr: NO },
      { name: '15 CEX integrations (CCXT) — spot on all 15',               haythix: UNQ, c3: LTD, ch: LTD, bg: LTD, pn: NO,  cr: LTD },
      { name: 'Perpetual futures on 8 CEX venues (up to 125x)',            haythix: UNQ, c3: LTD, ch: LTD, bg: LTD, pn: NO,  cr: NO },
      { name: 'Unified balance view across CEX + DEX + wallets',           haythix: UNQ, c3: NO,  ch: NO,  bg: LTD, pn: NO,  cr: NO },
      { name: 'MEXC integration (high-leverage perps with disclaimer)',    haythix: UNQ, c3: NO,  ch: NO,  bg: NO,  pn: NO,  cr: NO },
      { name: 'Gas price optimization',                                    haythix: YES, c3: NO,  ch: NO,  bg: NO,  pn: NO,  cr: NO },
      { name: 'Cross-exchange arbitrage',                                  haythix: YES, c3: LTD, ch: NO,  bg: YES, pn: LTD, cr: NO },
      { name: 'Token auto-discovery (Token Universe Scanner)',             haythix: YES, c3: NO,  ch: NO,  bg: NO,  pn: NO,  cr: NO },
      { name: 'Cross-chain portfolio view',                                haythix: YES, c3: NO,  ch: NO,  bg: NO,  pn: NO,  cr: NO },
      { name: 'Non-custodial (your keys, your funds)',                     haythix: YES, c3: NO,  ch: NO,  bg: NO,  pn: NO,  cr: NO },
    ],
  },
  {
    category: 'Strategy & Trading Tools',
    features: [
      { name: 'Strategy marketplace',            haythix: YES, c3: YES, ch: YES, bg: LTD, pn: NO,  cr: LTD },
      { name: 'Verified track record (real CCXT P&L)', haythix: UNQ, c3: NO,  ch: LTD, bg: NO,  pn: NO,  cr: NO },
      { name: 'TradingView chart embedded',       haythix: YES, c3: YES, ch: YES, bg: YES, pn: LTD, cr: LTD },
      { name: 'Drag-and-drop strategy builder',  haythix: YES, c3: NO,  ch: LTD, bg: NO,  pn: NO,  cr: YES },
      { name: 'Copy trading (leaderboard)',       haythix: YES, c3: LTD, ch: LTD, bg: NO,  pn: YES, cr: NO },
      { name: 'Paper trading / simulation',       haythix: YES, c3: YES, ch: YES, bg: LTD, pn: NO,  cr: LTD },
      { name: 'Backtesting engine',              haythix: YES, c3: YES, ch: YES, bg: YES, pn: NO,  cr: NO },
      { name: 'Real historical OHLCV backtest',   haythix: YES, c3: YES, ch: YES, bg: LTD, pn: NO,  cr: NO },
      { name: 'Portfolio rebalancing',            haythix: YES, c3: LTD, ch: LTD, bg: LTD, pn: NO,  cr: NO },
      { name: 'Rule-based automation (if/then)',  haythix: YES, c3: YES, ch: YES, bg: LTD, pn: LTD, cr: YES },
      { name: 'Template library (pre-built)',     haythix: YES, c3: LTD, ch: YES, bg: LTD, pn: YES, cr: YES },
      { name: 'Guided onboarding wizard',         haythix: YES, c3: LTD, ch: YES, bg: LTD, pn: YES, cr: LTD },
    ],
  },
  {
    category: 'Built-in Community: Learn From Real Traders (no competitor has this)',
    community: true,
    features: [
      { name: 'Live chat rooms for traders to share ideas, ask questions, learn — built into the platform', haythix: UNQ, c3: NO, ch: NO, bg: NO, pn: NO, cr: NO },
      { name: 'Per-chain rooms (Ethereum / Base / Solana / etc.) — find your tribe',                        haythix: UNQ, c3: NO, ch: NO, bg: NO, pn: NO, cr: NO },
      { name: 'Per-exchange rooms (Binance / Coinbase / Kraken / etc.) — venue-specific alpha',             haythix: UNQ, c3: NO, ch: NO, bg: NO, pn: NO, cr: NO },
      { name: 'Built-in community chat rooms',      haythix: YES, c3: NO,  ch: NO,  bg: NO,  pn: NO,  cr: NO },
      { name: 'Tier-gated exclusive rooms',          haythix: UNQ, c3: NO,  ch: NO,  bg: NO,  pn: NO,  cr: NO },
      { name: 'Verified call tracking & accuracy',   haythix: UNQ, c3: NO,  ch: NO,  bg: NO,  pn: NO,  cr: NO },
      { name: 'Following & leaderboards',            haythix: YES, c3: NO,  ch: NO,  bg: NO,  pn: LTD, cr: NO },
      { name: 'Community polls & predictions',       haythix: YES, c3: NO,  ch: NO,  bg: NO,  pn: NO,  cr: NO },
      { name: 'AI room summaries',                   haythix: UNQ, c3: NO,  ch: NO,  bg: NO,  pn: NO,  cr: NO },
      { name: 'Streaks & daily check-ins',           haythix: UNQ, c3: NO,  ch: NO,  bg: NO,  pn: NO,  cr: NO },
      { name: 'Reactions & @mentions',               haythix: YES, c3: NO,  ch: NO,  bg: NO,  pn: NO,  cr: NO },
      { name: 'Token project verification',          haythix: UNQ, c3: NO,  ch: NO,  bg: NO,  pn: NO,  cr: NO },
      { name: 'Tip jar (USDC / crypto tips)',        haythix: UNQ, c3: NO,  ch: NO,  bg: NO,  pn: NO,  cr: NO },
      { name: 'Paid Channels (creator monetization, 80/20 USDC)', haythix: UNQ, c3: NO, ch: NO, bg: NO, pn: NO, cr: NO },
      { name: 'Server-verified trade snapshots in chat', haythix: UNQ, c3: NO, ch: NO, bg: NO, pn: NO, cr: NO },
      { name: 'Voice notes in chat (30s)',           haythix: UNQ, c3: NO,  ch: NO,  bg: NO,  pn: NO,  cr: NO },
      { name: 'Top Calls of the Week (auto-pinned)', haythix: UNQ, c3: NO,  ch: NO,  bg: NO,  pn: NO,  cr: NO },
      { name: '🔥 Hot Channel badge (>10 msgs/min)', haythix: UNQ, c3: NO,  ch: NO,  bg: NO,  pn: NO,  cr: NO },
      { name: 'Typing indicators',                   haythix: YES, c3: NO,  ch: NO,  bg: NO,  pn: NO,  cr: NO },
      { name: 'Tip read receipts',                   haythix: UNQ, c3: NO,  ch: NO,  bg: NO,  pn: NO,  cr: NO },
      { name: 'Auto trade-call detection (auto-tracked)', haythix: UNQ, c3: NO, ch: NO, bg: NO, pn: NO, cr: NO },
      { name: 'Verified Caller badge (20 calls @ 60%+)', haythix: UNQ, c3: NO, ch: NO, bg: NO, pn: NO, cr: NO },
      { name: 'Channel discovery & creation',        haythix: YES, c3: NO,  ch: NO,  bg: NO,  pn: NO,  cr: NO },
      { name: 'Auto-moderation (scam detection)',    haythix: YES, c3: NO,  ch: NO,  bg: NO,  pn: NO,  cr: NO },
      { name: 'Community onboarding flow',           haythix: YES, c3: NO,  ch: NO,  bg: NO,  pn: NO,  cr: NO },
      { name: 'Referral rewards program',            haythix: YES, c3: LTD, ch: NO,  bg: NO,  pn: NO,  cr: NO },
    ],
  },
  {
    category: 'Crypto-Native Payments',
    community: true,
    features: [
      { name: 'One-click USDC pay (no card, no Stripe)',    haythix: UNQ, c3: NO, ch: NO, bg: NO, pn: NO, cr: NO },
      { name: 'Multi-chain payments (Base/Polygon/Arbitrum)', haythix: UNQ, c3: NO, ch: NO, bg: NO, pn: NO, cr: NO },
      { name: 'On-chain auto-renew (USDC allowance)',       haythix: UNQ, c3: NO, ch: NO, bg: NO, pn: NO, cr: NO },
      { name: 'Server-verified on-chain payments',          haythix: UNQ, c3: NO, ch: NO, bg: NO, pn: NO, cr: NO },
      { name: 'T-3d & T-1d renewal reminders',              haythix: YES, c3: LTD, ch: LTD, bg: LTD, pn: LTD, cr: LTD },
      { name: 'Credit card (Stripe) fallback',              haythix: YES, c3: YES, ch: YES, bg: YES, pn: YES, cr: YES },
      { name: 'Pay in USDC for subscription tiers',         haythix: UNQ, c3: NO, ch: NO, bg: NO, pn: NO, cr: NO },
    ],
  },
  {
    category: 'Engineering Integrity',
    community: false,
    features: [
      { name: 'Position integrity + drift detection',       haythix: UNQ, c3: NO, ch: NO, bg: NO, pn: NO, cr: NO },
      { name: 'Dual-view accounting (operational + tax FIFO/LIFO/HIFO)', haythix: UNQ, c3: NO, ch: NO, bg: NO, pn: NO, cr: NO },
      { name: 'Outbound webhooks (Discord/Telegram event broadcasting)', haythix: UNQ, c3: LTD, ch: LTD, bg: NO, pn: NO, cr: NO },
      { name: 'Real-history backtests only (no fake scenarios)', haythix: UNQ, c3: NO, ch: NO, bg: NO, pn: NO, cr: NO },
      { name: 'No infrastructure leaks in UI (provider names hidden)', haythix: UNQ, c3: NO, ch: NO, bg: NO, pn: NO, cr: NO },
      { name: 'Open audit log of all team/admin actions',    haythix: YES, c3: LTD, ch: LTD, bg: NO, pn: NO, cr: NO },
    ],
  },
  {
    category: 'Risk Management',
    features: [
      { name: '5-layer risk controls',        haythix: YES, c3: LTD, ch: LTD, bg: LTD, pn: LTD, cr: LTD },
      { name: 'Per-wallet spending limits',    haythix: YES, c3: NO,  ch: NO,  bg: NO,  pn: NO,  cr: NO },
      { name: 'Portfolio drawdown protection', haythix: YES, c3: LTD, ch: NO,  bg: NO,  pn: NO,  cr: LTD },
      { name: 'Emergency kill switch',         haythix: YES, c3: NO,  ch: NO,  bg: NO,  pn: NO,  cr: NO },
      { name: 'Volatility-based auto-pause',   haythix: YES, c3: NO,  ch: NO,  bg: NO,  pn: NO,  cr: NO },
      { name: 'Stop-loss / take-profit',       haythix: YES, c3: YES, ch: YES, bg: YES, pn: YES, cr: YES },
      { name: 'Trailing stop orders',          haythix: YES, c3: YES, ch: YES, bg: YES, pn: YES, cr: LTD },
    ],
  },
  {
    category: 'Elite Suite — 17 Institutional Tools (no other platform has these)',
    elite: true,
    features: [
      // Trader's Suite
      { name: 'Multi-Account Aggregator (unify CEX + DEX + wallets in one view)', haythix: UNQ, c3: NO, ch: NO, bg: NO, pn: NO, cr: NO },
      { name: 'Tax-Loss Harvest Engine (with curated replacement map)',           haythix: UNQ, c3: NO, ch: NO, bg: NO, pn: NO, cr: NO },
      { name: 'Institutional Reports (Bloomberg-grade PDF, audit-ready)',         haythix: UNQ, c3: NO, ch: NO, bg: NO, pn: NO, cr: NO },
      { name: 'AI Strategy Coach (grounded in YOUR portfolio data)',              haythix: UNQ, c3: NO, ch: NO, bg: NO, pn: NO, cr: NO },
      { name: 'Quant Lab (factor models, signal correlations, regression)',       haythix: UNQ, c3: NO, ch: NO, bg: NO, pn: NO, cr: NO },
      { name: 'Uniswap V3 LP Studio (range optimizer + IL calculator)',           haythix: UNQ, c3: NO, ch: NO, bg: NO, pn: NO, cr: NO },
      { name: 'Token Launch Scanner (24/7 honeypot detection across 7 chains)',   haythix: UNQ, c3: NO, ch: NO, bg: NO, pn: NO, cr: NO },
      // Issuer Tools
      { name: 'DEX Liquidity Optimizer (real math for target volume)',            haythix: UNQ, c3: NO, ch: NO, bg: NO, pn: NO, cr: NO },
      { name: 'Listing Readiness Scorecard (Binance/Coinbase/OKX/Kraken/Gemini)', haythix: UNQ, c3: NO, ch: NO, bg: NO, pn: NO, cr: NO },
      { name: 'Launch Volume Forecaster (real CoinGecko comparables)',            haythix: UNQ, c3: NO, ch: NO, bg: NO, pn: NO, cr: NO },
      { name: 'Launch Roadmap Planner (4 phased templates)',                      haythix: UNQ, c3: NO, ch: NO, bg: NO, pn: NO, cr: NO },
      { name: 'Volume Health Widget (embeddable SVG, hourly updates)',            haythix: UNQ, c3: NO, ch: NO, bg: NO, pn: NO, cr: NO },
      { name: 'Competitor Activity Tracker (5 bot-detection indicators)',         haythix: UNQ, c3: NO, ch: NO, bg: NO, pn: NO, cr: NO },
      // Tokenomics
      { name: 'AI Tokenomics Advisor (PDF whitepaper analysis)',                  haythix: UNQ, c3: NO, ch: NO, bg: NO, pn: NO, cr: NO },
      { name: 'Tokenomics Studio (build whitepaper-ready output)',                haythix: UNQ, c3: NO, ch: NO, bg: NO, pn: NO, cr: NO },
      // Exclusive access
      { name: 'Elite Lounge + Whale Club (private chat rooms)',                   haythix: UNQ, c3: NO, ch: NO, bg: NO, pn: NO, cr: NO },
      { name: 'Direct founder access + Elite-only reactions/badges',              haythix: UNQ, c3: NO, ch: NO, bg: NO, pn: NO, cr: NO },
    ],
  },
  {
    category: 'Platform & Security',
    features: [
      { name: 'MFA / TOTP security',           haythix: YES, c3: YES, ch: YES, bg: LTD, pn: YES, cr: LTD },
      { name: 'Cloud-encrypted key storage',    haythix: YES, c3: NO,  ch: NO,  bg: NO,  pn: NO,  cr: NO },
      { name: 'AES-256-GCM encryption (read-only keys)', haythix: UNQ, c3: LTD, ch: LTD, bg: LTD, pn: LTD, cr: LTD },
      { name: 'Real-time alerts (5 channels)',  haythix: YES, c3: LTD, ch: LTD, bg: LTD, pn: LTD, cr: YES },
      { name: 'Two-way Telegram trading commands', haythix: UNQ, c3: YES, ch: NO,  bg: NO,  pn: NO,  cr: NO },
      { name: 'API access',                    haythix: YES, c3: YES, ch: YES, bg: YES, pn: YES, cr: LTD },
      { name: 'Installable PWA (mobile)',      haythix: YES, c3: NO,  ch: LTD, bg: LTD, pn: NO,  cr: NO },
      { name: 'Native mobile app',             haythix: LTD, c3: YES, ch: YES, bg: YES, pn: YES, cr: LTD },
    ],
  },
  {
    category: 'Signature Power Features (only on haythix)',
    verticals: true,
    features: [
      { name: 'Wallet-as-Bot: signed safety policy with forbidden-ops floor that even admin can\'t override', haythix: UNQ, c3: NO, ch: NO, bg: NO, pn: NO, cr: NO },
      { name: 'Public safety-policy endpoint — verify our floor without an account',                          haythix: UNQ, c3: NO, ch: NO, bg: NO, pn: NO, cr: NO },
      { name: 'AI forecast accuracy with Wilson 95% confidence intervals on real OHLCV',                       haythix: UNQ, c3: NO, ch: NO, bg: NO, pn: NO, cr: NO },
      { name: 'Sample size visible on every forecast; small-n predictions automatically de-ranked',          haythix: UNQ, c3: NO, ch: NO, bg: NO, pn: NO, cr: NO },
      { name: 'Verified Creator badge — must hit 20 calls @ 60%+ accuracy across 30 days',                    haythix: UNQ, c3: NO, ch: NO, bg: NO, pn: NO, cr: NO },
      { name: 'Leaderboard ranked by Wilson lower bound, not vanity win-rate (no lucky single-call gurus)',    haythix: UNQ, c3: NO, ch: NO, bg: NO, pn: NO, cr: NO },
      { name: 'Tax-lot ledger reconstructed from real exchange fills (FIFO/LIFO/HIFO selectable)',             haythix: UNQ, c3: NO, ch: NO, bg: NO, pn: NO, cr: NO },
      { name: 'Tax-Loss Harvest with curated replacement map (ETH→WSTETH, USDC→USDT, etc.)',                  haythix: UNQ, c3: NO, ch: NO, bg: NO, pn: NO, cr: NO },
      { name: 'What-if Sharpe / VaR / drawdown computed over real Binance OHLCV (no fake scenarios)',         haythix: UNQ, c3: NO, ch: NO, bg: NO, pn: NO, cr: NO },
      { name: 'Tokenomics Studio — multi-token workspace with member roles + plan tiers',                     haythix: UNQ, c3: NO, ch: NO, bg: NO, pn: NO, cr: NO },
      { name: 'Live token holder analysis (Gini coefficient + concentration metrics from chain explorers)',   haythix: UNQ, c3: NO, ch: NO, bg: NO, pn: NO, cr: NO },
      { name: 'Public investor-report URLs (no-auth read-only, PII redacted) for token projects',             haythix: UNQ, c3: NO, ch: NO, bg: NO, pn: NO, cr: NO },
      { name: 'B2B API keys with SHA-256 hashing (plaintext shown ONCE, like AWS)',                            haythix: UNQ, c3: NO, ch: NO, bg: NO, pn: NO, cr: NO },
      { name: 'Cross-chain token scanner with universe stats per run',                                         haythix: UNQ, c3: NO, ch: NO, bg: NO, pn: NO, cr: NO },
      { name: 'SHA-256 methodology hash on every scan (reproducible audit trail)',                            haythix: UNQ, c3: NO, ch: NO, bg: NO, pn: NO, cr: NO },
      { name: 'Transparent "partial" status when data is incomplete (vs faking 100% completeness)',           haythix: UNQ, c3: NO, ch: NO, bg: NO, pn: NO, cr: NO },
      { name: '"Pending / unavailable" empty states across the platform (vs fake placeholder numbers)',       haythix: UNQ, c3: NO, ch: NO, bg: NO, pn: NO, cr: NO },
      { name: 'Public verifiable endpoint for every claim above — audit us yourself',                          haythix: UNQ, c3: NO, ch: NO, bg: NO, pn: NO, cr: NO },
    ],
  },
  {
    category: 'Pricing',
    pricing: true,
    features: [
      { name: 'Entry price (annual)',  haythix: '$10/mo',     c3: '$15/mo',  ch: '$24/mo',  bg: '$23/mo',  pn: 'Free',    cr: '$30/mo' },
      { name: 'Mid tier (annual)',     haythix: '$49.99/mo',  c3: '$79/mo',  ch: '$58/mo',  bg: '$49/mo',  pn: 'Free',    cr: '$59/mo' },
      { name: 'Pro tier (annual)',     haythix: '$75.99/mo',  c3: '$110/mo', ch: '$107/mo', bg: '$112/mo', pn: 'Free',    cr: '$449/mo' },
      { name: 'Elite / Enterprise',   haythix: '$149.99/mo', c3: 'N/A',     ch: 'N/A',     bg: 'N/A',     pn: 'N/A',     cr: 'N/A' },
      { name: 'Free trial',           haythix: '14 days',    c3: '3 days',  ch: '7 days',  bg: '7 days',  pn: 'Free',    cr: '7 days' },
      { name: 'Annual discount',      haythix: 'Up to 50%',  c3: '20%',     ch: '15%',     bg: '25%',     pn: 'N/A',     cr: '20%' },
      { name: 'Founders 50% forever', haythix: '✓ First 100', c3: 'N/A',   ch: 'N/A',     bg: 'N/A',     pn: 'N/A',     cr: 'N/A' },
    ],
  },
];

// ─── Count features per platform ────────────────────────────────────────────

function countScores() {
  let totals = { haythix: 0, c3: 0, ch: 0, bg: 0, pn: 0, cr: 0 };
  let featureCount = 0;

  SECTIONS.forEach(section => {
    if (section.pricing) return;
    section.features.forEach(f => {
      featureCount++;
      COMPETITORS.forEach(comp => {
        const v = f[comp.key];
        if (v === YES || v === UNQ) totals[comp.key]++;
        else if (v === LTD) totals[comp.key] += 0.5;
      });
    });
  });

  return { totals, featureCount };
}

const { totals: SCORES, featureCount: TOTAL_FEATURES } = countScores();

// Exported so other public pages (Features hero, Landing FAQ, etc.) display the
// SAME canonical count. ComparisonPage's SECTIONS is the source-of-truth catalog.
export { TOTAL_FEATURES };

// ─── Status icon component ──────────────────────────────────────────────────

function StatusCell({ value, ishaythix, isPricing }) {
  if (isPricing) {
    return (
      <span className={`text-[10px] sm:text-xs font-semibold ${ishaythix ? 'text-amber-400' : 'text-gray-400'}`}>
        {value}
      </span>
    );
  }

  switch (value) {
    case YES:
      return (
        <span className="inline-flex items-center justify-center w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-emerald-500/15">
          <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-400" />
        </span>
      );
    case NO:
      return (
        <span className="inline-flex items-center justify-center w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-dark-600/50">
          <X className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-600" />
        </span>
      );
    case LTD:
      return (
        <span className="inline-flex items-center justify-center w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-amber-500/15">
          <AlertCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-amber-400" />
        </span>
      );
    case UNQ:
      return (
        <span className="inline-flex items-center justify-center w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-gradient-to-br from-amber-500/20 to-orange-500/20 animate-pulse">
          <Sparkles className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-orange-400" />
        </span>
      );
    default:
      return <span className="text-gray-600">—</span>;
  }
}

// ─── Page component ──────────────────────────────────────────────────────────

export default function ComparisonPage() {
  const navigate = useNavigate();
  const compCount = COMPETITORS.length - 1; // exclude haythix

  return (
    <div className="min-h-screen bg-[#0a0b0f] text-white">
      <Seo
        title="haythix vs 3Commas, Cryptohopper, Bitsgap, Pionex, Coinrule"
        description="Side-by-side comparison of haythix vs the top crypto trading bot platforms. Unified DEX + CEX, multi-chain trading, AI signals, copy trading, community — features competitors charge extra for or do not offer."
        path="/comparison"
      />
      <PublicNav />

      {/* Hero */}
      <div className="text-center pt-16 pb-12 px-4">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-400/10 border border-amber-400/20 mb-6">
          <Trophy className="w-4 h-4 text-amber-400" />
          <span className="text-xs font-semibold text-amber-400 tracking-wide">ONE PLATFORM TO RULE THEM ALL</span>
        </div>
        <h1 className="text-4xl md:text-5xl font-bold mb-4">
          haythix vs <span className="bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">{compCount} top competitors</span>
        </h1>
        <p className="text-gray-400 text-lg max-w-3xl mx-auto mb-4">
          Every feature that 3Commas, Cryptohopper, Bitsgap, Pionex, and Coinrule offer separately —
          haythix combines into one platform. Plus AI intelligence, community, and Elite features
          you won't find anywhere else.
        </p>
        <p className="text-sm text-amber-400/80 font-medium mb-6">
          {TOTAL_FEATURES} features compared across {compCount + 1} platforms
        </p>

        {/* Verifiable endpoints banner */}
        <div className="max-w-3xl mx-auto mb-8 bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-5 text-left">
          <div className="flex items-start gap-3 mb-3">
            <Eye className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <div className="text-sm font-semibold text-white mb-1">Read the methodology before you sign up</div>
              <p className="text-xs text-gray-400 leading-relaxed">
                Every claim in the &ldquo;Seven Signature Verticals&rdquo; section below is backed by a public API endpoint
                you can open right now — no account required. If a number can&rsquo;t be calculated reliably, the
                endpoint returns <span className="text-amber-400 font-mono">pending</span> instead of guessing.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5 pl-8">
            {[
              { label: 'V1 Wallet Policy',     path: '/api/wallet-sessions/policy' },
              { label: 'V2 Forecast Accuracy', path: '/api/forecast-accuracy/summary' },
              { label: 'V3 Verified Creators', path: '/api/verified-creators/policy' },
              { label: 'V4 Tax Strategy',      path: '/api/tax-strategy/replacement-map' },
              { label: 'V5 Tokenomics Report', path: '/api/tokenomics/r/{slug}' },
              { label: 'V7 Scanner Policy',    path: '/api/scanner/policy' },
            ].map(v => (
              <a
                key={v.label}
                href={v.path.includes('{') ? undefined : `https://api.haythix.com${v.path}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-emerald-500/10 border border-emerald-500/20 text-[11px] text-emerald-300 hover:bg-emerald-500/15 hover:border-emerald-500/40 transition-colors font-mono"
                title={`Open ${v.path}`}
              >
                {v.label}
              </a>
            ))}
          </div>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap justify-center gap-3 text-xs text-gray-400">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-dark-800/50 border border-dark-700/30">
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-emerald-500/15">
              <Check className="w-3 h-3 text-emerald-400" />
            </span>
            Fully supported
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-dark-800/50 border border-dark-700/30">
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-500/15">
              <AlertCircle className="w-3 h-3 text-amber-400" />
            </span>
            Limited / partial
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-dark-800/50 border border-dark-700/30">
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-dark-600/50">
              <X className="w-3 h-3 text-gray-600" />
            </span>
            Not available
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-dark-800/50 border border-dark-700/30">
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gradient-to-br from-amber-500/20 to-orange-500/20">
              <Sparkles className="w-3 h-3 text-orange-400" />
            </span>
            haythix Exclusive
          </div>
        </div>
      </div>

      {/* Comparison table */}
      <div className="max-w-7xl mx-auto px-4 pb-12">
        <div className="overflow-x-auto rounded-2xl border border-dark-700/50">
          <table className="w-full text-sm min-w-[900px]">
            {/* Header */}
            <thead>
              <tr className="border-b border-dark-700/50">
                <th className="text-left px-4 py-5 text-gray-400 font-medium bg-dark-800/50 w-[240px] sticky left-0 z-10">
                  Feature
                </th>
                {COMPETITORS.map(comp => (
                  <th key={comp.key} className={`px-3 py-5 text-center ${
                    comp.highlight
                      ? 'bg-gradient-to-b from-amber-500/8 to-transparent border-x border-amber-400/10'
                      : 'bg-dark-800/50'
                  }`}>
                    <div className="flex flex-col items-center gap-1.5">
                      <span className={`font-bold text-sm ${comp.highlight ? 'text-amber-400' : 'text-gray-400'}`}>
                        {comp.name}
                      </span>
                      {comp.highlight && (
                        <span className="text-[8px] font-bold px-2.5 py-0.5 rounded-full bg-gradient-to-r from-amber-400 to-orange-400 text-gray-900 tracking-wider">
                          YOU ARE HERE
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {SECTIONS.map(section => (
                <>
                  {/* Category header */}
                  <tr key={`cat-${section.category}`} className="bg-dark-800/70">
                    <td colSpan={COMPETITORS.length + 1} className="px-4 py-3 text-left">
                      <div className="flex items-center gap-2.5">
                        {section.community && <Users className="w-3.5 h-3.5 text-violet-400" />}
                        {section.elite && <Crown className="w-3.5 h-3.5 text-amber-400" />}
                        {section.verticals && <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />}
                        {section.headline && <Sparkles className="w-3.5 h-3.5 text-cyan-400" />}
                        <span className={`text-xs font-bold tracking-widest uppercase ${
                          section.elite ? 'text-amber-400'
                            : section.community ? 'text-violet-400'
                            : section.verticals ? 'text-emerald-400'
                            : section.headline ? 'text-cyan-400'
                            : 'text-gray-300'
                        }`}>
                          {section.category}
                        </span>
                        {section.elite && (
                          <span className="text-[8px] font-bold px-2 py-0.5 rounded bg-gradient-to-r from-amber-400/20 to-orange-400/20 text-amber-400 border border-amber-400/25 tracking-wider">
                            ELITE ONLY
                          </span>
                        )}
                        {section.community && (
                          <span className="text-[8px] font-bold px-2 py-0.5 rounded bg-violet-400/15 text-violet-400 border border-violet-400/25 tracking-wider">
                            ONLY ON haythix
                          </span>
                        )}
                        {section.verticals && (
                          <span className="text-[8px] font-bold px-2 py-0.5 rounded bg-emerald-400/15 text-emerald-400 border border-emerald-400/25 tracking-wider">
                            UNIQUE — VERIFIABLE ENDPOINTS
                          </span>
                        )}
                        {section.headline && (
                          <span className="text-[8px] font-bold px-2 py-0.5 rounded bg-gradient-to-r from-cyan-400/20 to-emerald-400/20 text-cyan-300 border border-cyan-400/30 tracking-wider">
                            INDUSTRY FIRST
                          </span>
                        )}
                        {section.features && (
                          <span className="text-[9px] text-gray-600 ml-auto">
                            {section.features.length} features
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                  {/* Feature rows */}
                  {section.features.map((f, i) => (
                    <tr key={`${section.category}-${i}`} className={`border-b border-dark-700/20 hover:bg-white/[0.02] ${
                      section.elite ? 'bg-amber-400/[0.02]'
                        : section.community ? 'bg-violet-400/[0.01]'
                        : section.verticals ? 'bg-emerald-400/[0.02]'
                        : section.headline ? 'bg-cyan-400/[0.02]'
                        : ''
                    }`}>
                      <td className={`px-4 py-2.5 text-left sticky left-0 z-10 bg-[#0a0b0f] ${
                        section.elite ? 'text-amber-300 font-medium'
                          : section.community ? 'text-violet-300'
                          : section.verticals ? 'text-emerald-300 font-medium'
                          : section.headline ? 'text-cyan-300 font-medium'
                          : 'text-gray-300'
                      }`}>
                        {section.elite && <Star className="w-3 h-3 inline mr-1.5 text-amber-400 fill-amber-400" />}
                        {section.verticals && <ShieldCheck className="w-3 h-3 inline mr-1.5 text-emerald-400" />}
                        {section.headline && <Sparkles className="w-3 h-3 inline mr-1.5 text-cyan-400" />}
                        {f.name}
                      </td>
                      {COMPETITORS.map(comp => (
                        <td key={comp.key} className={`px-3 py-2.5 text-center ${
                          comp.highlight ? 'border-x border-amber-400/5 bg-amber-400/[0.02]' : ''
                        }`}>
                          <StatusCell
                            value={f[comp.key]}
                            ishaythix={comp.highlight}
                            isPricing={section.pricing}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Score summary */}
      <div className="max-w-7xl mx-auto px-4 pb-12">
        <h2 className="text-center text-lg font-bold text-gray-300 mb-6">Feature Score Comparison</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {COMPETITORS.map(comp => {
            const score = Math.round(SCORES[comp.key]);
            const pct = (score / TOTAL_FEATURES) * 100;
            const isMT = comp.highlight;
            return (
              <div key={comp.key} className={`rounded-xl border p-4 text-center ${
                isMT
                  ? 'bg-amber-400/10 border-amber-400/20 ring-1 ring-amber-400/10'
                  : 'bg-dark-800/50 border-dark-700/30'
              }`}>
                {isMT && (
                  <div className="flex justify-center mb-2">
                    <Trophy className="w-5 h-5 text-amber-400" />
                  </div>
                )}
                <p className={`text-2xl font-bold ${isMT ? 'text-amber-400' : 'text-gray-400'}`}>
                  {score}/{TOTAL_FEATURES}
                </p>
                <p className="text-xs text-gray-500 mt-1">{comp.name}</p>
                <p className={`text-[10px] mt-0.5 ${isMT ? 'text-amber-400/70' : 'text-gray-600'}`}>
                  {Math.round(pct)}% coverage
                </p>
                <div className="w-full h-1.5 rounded-full bg-dark-700 mt-2.5">
                  <div
                    className={`h-full rounded-full transition-all duration-1000 ${
                      isMT ? 'bg-gradient-to-r from-amber-400 to-orange-400' : 'bg-gray-600'
                    }`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* "What you'd need" callout */}
      <div className="max-w-4xl mx-auto px-4 pb-12">
        <div className="rounded-2xl bg-gradient-to-br from-dark-800/80 to-dark-900/50 border border-dark-700/50 p-6 md:p-8">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-amber-400/10 flex items-center justify-center">
              <Shield className="w-6 h-6 text-amber-400" />
            </div>
            <div>
              <h3 className="text-lg font-bold mb-2">To match haythix, you'd need all 5 platforms combined</h3>
              <div className="text-sm text-gray-400 space-y-1.5">
                <p><span className="text-gray-300 font-medium">3Commas</span> for bot automation + DCA</p>
                <p><span className="text-gray-300 font-medium">Cryptohopper</span> for marketplace + signals</p>
                <p><span className="text-gray-300 font-medium">Bitsgap</span> for grid trading + arbitrage</p>
                <p><span className="text-gray-300 font-medium">Pionex</span> for free built-in bots</p>
                <p><span className="text-gray-300 font-medium">Coinrule</span> for rule-based automation</p>
                <p className="pt-2 text-amber-400/80 font-medium">
                  Combined cost: $200+/mo — and you'd still miss AI intelligence, community, DEX trading, and Elite features.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="max-w-4xl mx-auto px-4 pb-20">
        <div className="rounded-2xl bg-gradient-to-r from-amber-500/10 via-orange-500/5 to-violet-500/10 border border-amber-400/20 p-8 md:p-12 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-3">
            Why pay for 5 platforms when one does it all?
          </h2>
          <p className="text-gray-400 mb-6 max-w-lg mx-auto">
            Start with Starter at $10/mo or unlock everything with Elite.
            First 100 founders get 50% off forever.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              onClick={() => navigate('/pricing')}
              className="px-6 py-3 rounded-xl text-sm font-medium text-gray-300 border border-dark-600 hover:text-white hover:border-dark-500 transition-colors flex items-center gap-2"
            >
              View Pricing <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => navigate('/register?plan=trial')}
              className="px-6 py-3 rounded-xl bg-gradient-to-r from-amber-400 to-orange-400 text-gray-900 font-semibold text-sm hover:from-amber-300 hover:to-orange-300 transition-all flex items-center gap-2"
            >
              Start 14-Day Free Trial <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <PublicFooter />
    </div>
  );
}
